import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, Bell, Settings as SettingsIcon, Plus, Activity, ChevronLeft, ChevronRight, RefreshCw, Sun, Moon, Sparkles, X, Target, TrendingUp } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import TopTicker from "./TopTicker";
import AssetWatchlist from "./AssetWatchlist";

export default function Layout({
  children, onAddSetup, onStatusUpdate, sidebarOpen, onSidebarToggle,
  theme = "dark", onToggleTheme,
  // Watchlist props
  setups = [], allSetups = [], selectedId, onSelect,
  filter = "", onFilterChange,
  category = "ALLE", onCategoryChange, categories = [],
  onlyAttractive = false, onToggleAttractive,
  attractiveCount = 0, polling = false, onPoll,
}) {
  const location = useLocation();
  const isDashboard = location.pathname === "/";

  const navItem = (to, icon, label, testId) => {
    const Icon = icon;
    const active = location.pathname === to || (to !== "/" && location.pathname.startsWith(to));
    return (
      <NavLink
        to={to}
        data-testid={testId}
        title={label}
        className={`flex items-center gap-3 px-3 py-2 rounded-sm transition-colors ${
          active ? "bg-zinc-800 text-white" : "text-zinc-400 hover:bg-zinc-900 hover:text-white"
        }`}
      >
        <Icon size={16} strokeWidth={1.75} className="shrink-0" />
        {sidebarOpen && <span className="font-display font-medium text-sm">{label}</span>}
      </NavLink>
    );
  };

  const sidebarContent = (
    <div className="flex flex-col h-full">
      {/* Nav items */}
      <div className="p-2 space-y-1 shrink-0">
        {navItem("/", LayoutDashboard, "Dashboard", "nav-dashboard")}
        {navItem("/alerts", Bell, "Alerts", "nav-alerts")}
        {navItem("/settings", SettingsIcon, "Einstellungen", "nav-settings")}
      </div>

      {/* Watchlist — only on Dashboard and when sidebar is open */}
      {sidebarOpen && isDashboard && (
        <div className="flex flex-col flex-1 min-h-0 border-t border-zinc-800 mt-1">
          {/* Watchlist header */}
          <div className="p-3 border-b border-zinc-800 shrink-0 bg-zinc-950">
            <div className="flex items-center justify-between mb-2">
              <div>
                <div className="font-display font-bold text-sm tracking-tight">Watchlist</div>
                <div className="text-[10px] text-zinc-500 font-mono-num">{setups.length} / {allSetups.length} Setups</div>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={onPoll}
                disabled={polling || allSetups.length === 0}
                className="h-7 px-2 border-zinc-700 bg-transparent hover:bg-zinc-800 text-[10px]"
                data-testid="btn-poll-now"
                title="Preise aktualisieren"
              >
                <RefreshCw size={12} className={polling ? "animate-spin" : ""} />
              </Button>
            </div>
            <Input
              placeholder="Suche..."
              value={filter}
              onChange={(e) => onFilterChange(e.target.value)}
              className="bg-zinc-900 border-zinc-800 h-8 text-xs"
              data-testid="search-setups"
            />
            <button
              onClick={onToggleAttractive}
              data-testid="toggle-only-attractive"
              className={`mt-2 w-full px-2 py-1.5 text-[11px] font-display font-bold uppercase tracking-wider rounded-sm flex items-center justify-center gap-1.5 transition-colors border ${
                onlyAttractive
                  ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300"
                  : "bg-zinc-900 border-zinc-800 text-zinc-400 hover:bg-zinc-800"
              }`}
            >
              <Sparkles size={11} className={onlyAttractive ? "text-emerald-400" : ""} />
              Nur attraktiv ({attractiveCount})
            </button>
            <div className="flex flex-wrap gap-1 mt-2">
              {categories.map(c => (
                <button
                  key={c}
                  onClick={() => onCategoryChange(c)}
                  data-testid={`cat-filter-${c}`}
                  className={`px-2 py-0.5 text-[10px] font-display font-bold uppercase tracking-wider rounded-sm ${
                    category === c ? "bg-white text-black" : "bg-zinc-900 text-zinc-400 hover:bg-zinc-800"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
          {/* Scrollable list */}
          <div className="flex-1 overflow-y-auto">
            <AssetWatchlist setups={setups} selectedId={selectedId} onSelect={onSelect} />
          </div>
          {/* Quick stats */}
          <div className="shrink-0 border-t border-zinc-800 p-3 space-y-1.5">
            <div className="text-[9px] uppercase tracking-widest text-zinc-600 font-display mb-2">Übersicht</div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="flex items-center gap-1.5 text-zinc-500"><Target size={10} /> In / nahe Zone</span>
              <span className="font-mono-num font-bold text-emerald-400">{attractiveCount}</span>
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="flex items-center gap-1.5 text-zinc-500"><Activity size={10} /> Aktiv</span>
              <span className="font-mono-num font-bold text-zinc-300">{allSetups.filter(s => s.status !== "closed").length}</span>
            </div>
            <div className="flex items-center justify-between text-[11px]">
              <span className="flex items-center gap-1.5 text-zinc-500"><TrendingUp size={10} /> Gesamt</span>
              <span className="font-mono-num font-bold text-zinc-400">{allSetups.length}</span>
            </div>
          </div>
        </div>
      )}

      {/* Bottom: powered by (only when open, no watchlist) */}
      {sidebarOpen && !isDashboard && (
        <div className="mt-auto p-3 pt-4">
          <div className="text-[10px] uppercase tracking-widest text-zinc-600 font-display px-3 mb-1">Friedrich·Trade</div>
          <div className="text-[10px] text-zinc-500 font-mono-num px-3">Allocation Hub v1.0</div>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-app text-app flex flex-col">
      {/* Top bar */}
      <header className="h-14 border-b border-zinc-800 px-4 flex items-center justify-between bg-app sticky top-0 z-30 gap-4">
        <div className="flex items-center gap-3 shrink-0">
          <button
            onClick={onSidebarToggle}
            className="p-1.5 hover:bg-zinc-800 rounded-sm transition-colors"
            data-testid="btn-sidebar-toggle"
            title={sidebarOpen ? "Sidebar einklappen" : "Sidebar ausklappen"}
          >
            {sidebarOpen ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
          </button>
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-white text-black flex items-center justify-center font-display font-black text-sm">F</div>
            <div className="font-display font-bold tracking-tight text-base whitespace-nowrap">
              FRIEDRICH<span className="text-zinc-500">·TRADE</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-2 text-xs text-zinc-500 ml-2">
            <Activity size={12} className="text-emerald-500" />
            <span className="font-mono-num">LIVE</span>
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={onToggleTheme}
            data-testid="btn-theme-toggle"
            title={theme === "dark" ? "Light Mode" : "Dark Mode"}
            className="h-9 w-9 flex items-center justify-center border border-zinc-700 rounded-sm hover:bg-zinc-800 transition-colors text-zinc-300"
          >
            {theme === "dark" ? <Sun size={14} /> : <Moon size={14} />}
          </button>
          <Button
            data-testid="header-status-update-btn"
            onClick={onStatusUpdate}
            variant="outline"
            className="border-emerald-600/40 bg-emerald-600/10 hover:bg-emerald-600/20 text-emerald-400 rounded-sm font-display font-bold tracking-wide text-xs uppercase h-9 px-3"
          >
            <RefreshCw size={14} strokeWidth={2.5} className="mr-1" />
            <span className="hidden sm:inline">Status Update</span>
            <span className="sm:hidden">Status</span>
          </Button>
          <Button
            data-testid="header-add-setup-btn"
            onClick={onAddSetup}
            className="bg-white text-black hover:bg-zinc-200 rounded-sm font-display font-bold tracking-wide text-xs uppercase h-9 px-4"
          >
            <Plus size={14} strokeWidth={2.5} className="mr-1" />
            Setup
          </Button>
        </div>
      </header>

      {/* Live ticker bar */}
      <TopTicker />

      <div className="flex-1 flex relative">
        {/* Mobile overlay backdrop */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-black/60 z-20 lg:hidden"
            onClick={onSidebarToggle}
          />
        )}

        {/* Sidebar — mobile: fixed overlay, desktop: static */}
        <aside
          className={`
            border-r border-zinc-800 bg-zinc-950 flex flex-col shrink-0 transition-all duration-200
            lg:static lg:translate-x-0
            fixed top-[5.75rem] left-0 bottom-0 z-20
            ${sidebarOpen
              ? "translate-x-0 w-72"
              : "-translate-x-full lg:translate-x-0 w-72 lg:w-14"
            }
          `}
        >
          {/* Mobile close button */}
          {sidebarOpen && (
            <button
              onClick={onSidebarToggle}
              className="lg:hidden absolute top-2 right-2 p-1 hover:bg-zinc-800 rounded-sm text-zinc-400"
            >
              <X size={14} />
            </button>
          )}
          {sidebarContent}
        </aside>

        {/* Main content */}
        <main className="flex-1 min-w-0">
          {children}
        </main>
      </div>
    </div>
  );
}
